import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Fires a GA4 page_view on every React Router navigation.
 *
 * Single-page apps only load index.html once, so Google Analytics would
 * otherwise record a single page view for the entire session. This hook
 * listens for route changes and sends an explicit page_view each time.
 *
 * The gtag base snippet lives in public/index.html with
 * `send_page_view: false`, so this hook is the single source of page views.
 * If GA isn't configured yet (or is blocked), window.gtag is undefined and
 * this no-ops safely.
 */
const useAnalytics = () => {
    const location = useLocation();

    useEffect(() => {
        if (typeof window.gtag !== 'function') return;

        const pagePath = location.pathname + location.search;
        // Defer to the next tick so usePageSeo (which runs in a sibling/child
        // effect during the same commit) has already updated document.title.
        const timer = setTimeout(() => {
            window.gtag('event', 'page_view', {
                page_path: pagePath,
                page_location: window.location.href,
                page_title: document.title,
            });
        }, 0);

        return () => clearTimeout(timer);
    }, [location.pathname, location.search]);
};

export default useAnalytics;
